import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const fmt = (v) => v?.toLocaleString?.() ?? v ?? 0;

const StatCard = ({ title, value, iconPath, color }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
      </svg>
    </div>
    <div>
      <p className="text-xl font-bold text-slate-900 leading-none">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{title}</p>
    </div>
  </div>
);

const Deletions = () => {
  const [summary, setSummary] = useState({ total_photos_deleted: 0, total_batches_deleted: 0, total_users_affected: 0, total_deletions: 0, last_deletion_at: null });
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchSummary = async () => {
    const res = await axios.get(`${API_URL}/logs/deletions/summary`);
    setSummary(res.data?.summary || {});
  };
  const fetchRecords = async (p = 1) => {
    const res = await axios.get(`${API_URL}/logs/deletions`, { params: { page: p, limit } });
    setRecords(res.data?.data || []);
    setTotalPages(res.data?.totalPages || 1);
  };
  const loadData = async (p = 1) => {
    try { setLoading(true); setError(''); await Promise.all([fetchSummary(), fetchRecords(p)]); }
    catch (err) { setError(err.response?.data?.error || 'Failed to load deletions'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(page); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const paginationButtons = useMemo(() => {
    const maxBtns = 5;
    let start = Math.max(1, page - Math.floor(maxBtns / 2));
    let end = Math.min(totalPages, start + maxBtns - 1);
    if (end - start < maxBtns - 1) start = Math.max(1, end - maxBtns + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [page, totalPages]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Deletion Analytics</h1>
        <p className="text-sm text-slate-500 mt-0.5">Full audit trail of every destructive action</p>
      </div>

      {error && (
        <div className="flex items-start gap-2.5 p-4 bg-rose-50 border border-rose-200 rounded-2xl">
          <svg className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-rose-700 text-sm">{error}</p>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard title="Photos Deleted" value={fmt(summary.total_photos_deleted)} color="bg-rose-600"
          iconPath="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        <StatCard title="Batches Removed" value={fmt(summary.total_batches_deleted)} color="bg-slate-700"
          iconPath="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        <StatCard title="Users Affected" value={fmt(summary.total_users_affected)} color="bg-amber-600"
          iconPath="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        <StatCard title="Last Deletion" value={summary.last_deletion_at ? new Date(summary.last_deletion_at).toLocaleDateString() : 'Never'} color="bg-violet-600"
          iconPath="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-bold text-slate-800">Deletion Log</h2>
            <p className="text-xs text-slate-400 mt-0.5">Page {page} of {totalPages} · {records.length} records shown</p>
          </div>
          <button onClick={() => loadData(page)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-xl text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-slate-100">
                {['When', 'Admin', 'Primary User', 'Photos', 'Batches', 'Users', 'Details'].map((h, i) => (
                  <th key={h} className={`px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide ${i >= 3 && i <= 5 ? 'text-center' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {records.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400 text-sm">No deletions recorded yet.</td></tr>
              ) : records.map((entry) => {
                let detailObj = null;
                if (entry.details) {
                  try { detailObj = typeof entry.details === 'string' ? JSON.parse(entry.details) : entry.details; } catch (e) { detailObj = null; }
                }
                return (
                  <tr key={entry.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-800">{new Date(entry.created_at).toLocaleDateString()}</p>
                      <p className="text-xs text-slate-400">{new Date(entry.created_at).toLocaleTimeString()}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-800">{entry.admin_username || 'Unknown'}</p>
                      <p className="text-xs text-slate-400">ID: {entry.admin_id ?? '—'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-slate-700">{entry.user_name || '—'}</p>
                      {entry.user_email && <p className="text-xs text-slate-400">{entry.user_email}</p>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-rose-100 text-rose-700">{fmt(entry.photos_deleted)}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-700">{fmt(entry.batches_deleted)}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-violet-100 text-violet-700">{fmt(entry.users_affected)}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {detailObj ? (
                        <div className="space-y-0.5">
                          {detailObj.batch_id && <p>Batch #{detailObj.batch_id}</p>}
                          {detailObj.errors?.length > 0 && <p className="text-rose-500">Errors: {detailObj.errors.slice(0, 2).join(', ')}{detailObj.errors.length > 2 ? '…' : ''}</p>}
                        </div>
                      ) : <span className="text-slate-300 italic">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {records.length > 0 && totalPages > 1 && (
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between gap-3">
            <p className="text-xs text-slate-400">{(page - 1) * limit + 1}–{(page - 1) * limit + records.length} of {summary.total_deletions || records.length}</p>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1.5 border border-slate-200 rounded-xl text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">← Prev</button>
              {paginationButtons.map((btn) => (
                <button key={btn} onClick={() => setPage(btn)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-medium ${btn === page ? 'text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  style={btn === page ? { background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' } : {}}>
                  {btn}
                </button>
              ))}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-3 py-1.5 border border-slate-200 rounded-xl text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">Next →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Deletions;
